import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { useToast } from '@/shared/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { tasksService } from '../api/tasks.service';
import { TASK_KEYS } from './queryKeys';
import type { Task } from '../types';

export function useArchiveTask() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  return useMutation({
    mutationFn: (taskId: string) => tasksService.archiveTask(taskId),

    onSuccess: (_, taskId) => {
      toast.success({ title: t('archive.successToast') });
      const entity = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, taskId));
      const targetKey = entity?.projectId
        ? TASK_KEYS.projectRoot(orgId, entity.projectId)
        : TASK_KEYS.byOrg(orgId);
      queryClient.invalidateQueries({ queryKey: targetKey });
    },
  });
}

export function useUnarchiveTask() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId)!;
  const { t } = useTranslation('tasks');
  const { toast } = useToast();

  return useMutation({
    mutationFn: (taskId: string) => tasksService.unarchiveTask(taskId),

    onSuccess: (_, taskId) => {
      toast.success({ title: t('unarchive.successToast') });
      const entity = queryClient.getQueryData<Task>(TASK_KEYS.entity(orgId, taskId));
      const targetKey = entity?.projectId
        ? TASK_KEYS.projectRoot(orgId, entity.projectId)
        : TASK_KEYS.byOrg(orgId);
      queryClient.invalidateQueries({ queryKey: targetKey });
    },
  });
}
